import interstellarApi from './interstellarApi';
import { AccountsListResponse, InterstellarAccount } from './types';

function parseAccountListPayload(data: unknown): InterstellarAccount[] {
  if (Array.isArray(data)) {
    return data as InterstellarAccount[];
  }
  if (data && typeof data === 'object') {
    const body = data as AccountsListResponse & {
      data?: InterstellarAccount[];
    };
    if (Array.isArray(body.accounts)) {
      return body.accounts;
    }
    if (Array.isArray(body.results)) {
      return body.results;
    }
    if (Array.isArray(body.data)) {
      return body.data;
    }
  }
  return [];
}

function getListMeta(data: unknown): AccountsListResponse | null {
  if (!data || Array.isArray(data) || typeof data !== 'object') {
    return null;
  }
  return data as AccountsListResponse;
}

const DEFAULT_PAGE_SIZE = 100;
const MAX_PAGES = 50;

/**
 * Lists accounts from Interstellar: GET /account/?page=&page_size=
 */
export const fetchAccounts = async (): Promise<InterstellarAccount[]> => {
  try {
    const all: InterstellarAccount[] = [];
    let page = 1;

    while (page <= MAX_PAGES) {
      const response = await interstellarApi.get<AccountsListResponse | InterstellarAccount[]>(
        '/account/',
        {
          params: { page, page_size: DEFAULT_PAGE_SIZE },
        }
      );

      const batch = parseAccountListPayload(response.data);
      if (batch.length === 0) {
        break;
      }
      all.push(...batch);

      const meta = getListMeta(response.data);
      const total = meta?.total_count;
      const pageSize = meta?.page_size ?? DEFAULT_PAGE_SIZE;

      if (total != null && all.length >= total) {
        break;
      }
      if (batch.length < pageSize) {
        break;
      }

      page += 1;
    }

    return all;
  } catch (error) {
    console.error('Error fetching accounts from Interstellar:', error);
    throw error;
  }
};

export const fetchAccount = async (accountId: number): Promise<InterstellarAccount> => {
  try {
    const response = await interstellarApi.get<InterstellarAccount>(`/account/${accountId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching account from Interstellar:', error);
    throw error;
  }
};

export interface GeolocationPatchPayload {
  latitude: number;
  longitude: number;
}

/** Respuesta de `PUT /account/` en Interstellar (no devuelve el registro completo) */
export interface UpdateAccountResponse {
  message: string;
  id: number;
}

/**
 * Georreferencia vía Interstellar `PUT /account/`:
 * `account_id` va en query (super admin / API key); `latitude` y `longitude` también como query.
 * @see interstellar/services/account_service/endpoints.py — `update_account`
 */
export const patchAccountGeolocation = async (
  accountId: number,
  payload: GeolocationPatchPayload
): Promise<UpdateAccountResponse> => {
  const response = await interstellarApi.put<UpdateAccountResponse>('/account/', null, {
    params: {
      account_id: accountId,
      latitude: payload.latitude,
      longitude: payload.longitude,
    },
  });
  return response.data;
};

export interface UpdateAccountPhoneNumbersPayload {
  new_phone_number?: string;
  new_call_rescue_number?: number;
}

/**
 * Actualiza la línea principal y/o el número de rescate de una cuenta.
 * @see interstellar/services/account_service/endpoints.py — `update_account`
 */
export const updateAccountPhoneNumbers = async (
  accountId: number,
  payload: UpdateAccountPhoneNumbersPayload
): Promise<UpdateAccountResponse> => {
  const response = await interstellarApi.put<UpdateAccountResponse>('/account/', null, {
    params: {
      account_id: accountId,
      ...(payload.new_phone_number && { new_phone_number: payload.new_phone_number }),
      ...(payload.new_call_rescue_number && {
        new_call_rescue_number: payload.new_call_rescue_number,
      }),
    },
  });
  return response.data;
};

/** Un gateway de mod_sofia tal como lo reporta la instancia de FreeSWITCH de la cuenta. */
export interface SipTrunkGateway {
  name: string;
  profile?: string;
  realm?: string;
  proxy?: string;
  from?: string;
  register?: string;
  state?: string;
  status?: string;
  'ping-time'?: string;
}

/**
 * Una ruta del dialplan que sale de la instancia: `host` cuando hace bridge
 * directo a una dirección (la PBX del cliente, un trunk sin gateway detrás),
 * `gateway` cuando pasa por uno.
 */
export interface SipTrunkDialplanDestination {
  extension: string;
  pattern?: string;
  gateway?: string;
  profile?: string;
  host?: string;
}

/** Respuesta de `POST /account/sip-trunk` en Interstellar. */
export interface RefreshSipTrunkResponse {
  id: number;
  sip_trunk: string | null;
  gateways: SipTrunkGateway[];
  dialplan_destinations: SipTrunkDialplanDestination[];
  outbound_proxy: string | null;
}

/**
 * Relee el SIP trunk de la cuenta desde su FreeSWITCH y lo guarda en Interstellar.
 * Cubre las tres formas en que una instancia saca llamadas: gateways de mod_sofia,
 * rutas del dialplan que hacen bridge directo a un host, y el outbound proxy del
 * perfil `external`. Todo eso vive en la instancia, así que este endpoint es la
 * única forma de llenar la columna sin editarla a mano. No modifica FreeSWITCH.
 * @see interstellar/services/account_service/endpoints.py — `refresh_account_sip_trunk`
 */
export const refreshAccountSipTrunk = async (
  accountId: number
): Promise<RefreshSipTrunkResponse> => {
  const response = await interstellarApi.post<RefreshSipTrunkResponse>('/account/sip-trunk', null, {
    params: { account_id: accountId },
  });
  return response.data;
};

/**
 * What to tell the operator after a refresh. An instance with no gateway loaded
 * is not a failure — it is the shape of an account whose outbound goes through
 * Kamailio, so the proxy is named instead of reporting nothing.
 */
export const sipTrunkToastMessage = (result: RefreshSipTrunkResponse): string => {
  if (result.sip_trunk) return `SIP trunk actualizado: ${result.sip_trunk}`;
  if (result.outbound_proxy) {
    return `Sin gateway propio; el saliente pasa por ${result.outbound_proxy}`;
  }
  return 'El FreeSWITCH de la cuenta no tiene ningún gateway SIP cargado';
};

/** A number that comes in, and the call module it lands on. */
export interface AccountRoutingInbound {
  number: string;
  call_module_id: number;
  call_module: string;
  schedule: string | null;
  /** `<schedule_type>:<config_type>`, e.g. `in_office:agent_route`. */
  routes: string[];
}

/** An extension of the account, with its registration if it is online right now. */
export interface AccountRoutingExtension {
  extension: number;
  name: string | null;
  is_active: boolean;
  /**
   * The devices the extension is registered from right now: one extension can be
   * registered from the browser softphone and a desk phone at the same time.
   * Optional because an instance of Interstellar older than the one that reports
   * every device omits it, and an absent field must not read as an empty list.
   */
  registrations?: AccountRoutingRegistration[];
}

export interface AccountRoutingRegistration {
  extension: string;
  realm: string | null;
  /** `ws` is the browser softphone; `udp`/`tls` is a desk phone; null is unknown. */
  transport: string | null;
}

/** A trunk calls leave through: a mod_sofia gateway, or a bridge straight at a host. */
export interface AccountRoutingOutbound {
  kind: 'gateway' | 'host';
  name: string;
  host: string | null;
  register?: boolean;
  state?: string | null;
  calls_in?: string | null;
  calls_out?: string | null;
  /**
   * Calls the route carried within `recent_window_days`, counted from the CDR.
   * Null when nothing was measured. The gateway counters cannot answer this on
   * their own: they are cumulative and no carrier switch resets them, so a trunk
   * left in the configuration keeps every call it ever placed.
   */
  recent_calls?: number | null;
  /** Dialplan expressions that reach it, e.g. `^9(\d{4})$`. */
  patterns: string[];
}

export interface AccountRouting {
  id: number;
  name: string;
  freeswitch_host: string;
  outbound_proxy: string | null;
  /** Window `recent_calls` covers; absent from an interstellar that predates it. */
  recent_window_days?: number;
  inbound: AccountRoutingInbound[];
  internal: AccountRoutingExtension[];
  outbound: AccountRoutingOutbound[];
  /** Legs the instance could not answer, e.g. `outbound_unavailable`. */
  warnings: string[];
}

/**
 * Reads how calls reach and leave an account: GET /account/routing
 *
 * Live, not stored: who is registered and what state a gateway is in expire in
 * seconds. The `sip_trunk` column stays a summary for the list.
 * @see interstellar/services/account_service/endpoints.py — `get_account_routing`
 */
export const fetchAccountRouting = async (accountId: number): Promise<AccountRouting> => {
  const response = await interstellarApi.get<AccountRouting>('/account/routing', {
    params: { account_id: accountId },
  });
  return response.data;
};

/** Carrier the account's outbound calls are dialled through. */
export type OutboundCarrier = 'twilio' | 'telnyx';

/** Payload for POST /account/ in Interstellar */
export interface CreateAccountPayload {
  name: string;
  phone_number: number;
  call_rescue_number: number;
  cosmos_account_id: string;
  /**
   * UUID naming the account's directory in the FreeSWITCH EFS mount, not the EC2 instance id.
   * Required for dedicated FreeSWITCH; omit when using `freeswitch_group_id`.
   */
  freeswitch_id?: string;
  /**
   * FreeSWITCH API URI. Required for dedicated FreeSWITCH; omit when using `freeswitch_group_id`.
   */
  freeswitch_uri?: string;
  /** DSN of a manually provisioned FreeSWITCH; omit when `freeswitch_client_name` or `freeswitch_group_id` is sent. */
  freeswitch_pg_connection_string?: string;
  /**
   * Client name of a stack provisioned through `/infrastructure/freeswitch`. Interstellar
   * reads the generated password from Secrets Manager, so it never reaches the browser.
   */
  freeswitch_client_name?: string;
  /**
   * ID of the FreeSWITCH group this account will share. When set, the account uses the
   * group's FreeSWITCH instance instead of a dedicated one.
   */
  freeswitch_group_id?: number;
  admin_username: string;
  admin_password: string;
  search_index_name?: string;
  sip_trunk?: string;
  /** Defaults to 'twilio'; the FreeSWITCH must have the carrier's gateway or creation fails with 409. */
  outbound_carrier?: OutboundCarrier;
}

/** Response from POST /account/ */
export interface CreateAccountResponse {
  message: string;
  id: number;
}

/**
 * Creates a new account in Interstellar: POST /account/
 * @see interstellar/services/account_service/endpoints.py — `create_account`
 */
export const createAccount = async (
  payload: CreateAccountPayload
): Promise<CreateAccountResponse> => {
  const response = await interstellarApi.post<CreateAccountResponse>('/account/', payload);
  return response.data;
};

/** Response from DELETE /account/ */
export interface DeleteAccountResponse {
  id: number;
  message: string;
}

/**
 * Deletes an account from Interstellar: DELETE /account/
 * @see interstellar/services/account_service/endpoints.py — `delete_account`
 */
export const deleteAccount = async (accountId: number): Promise<DeleteAccountResponse> => {
  const response = await interstellarApi.delete<DeleteAccountResponse>('/account/', {
    params: { account_id: accountId },
  });
  return response.data;
};

/** Payload for POST /account/configure-freeswitch */
export interface ConfigureFreeSwitchPayload {
  module_id: number;
}

/** Response from POST /account/configure-freeswitch */
export interface ConfigureFreeSwitchResponse {
  id: number;
  name: string;
  freeswitch_host: string;
  configured: boolean;
  provision_status: {
    pending_manual: string[];
  };
}

/**
 * Triggers FreeSWITCH configuration after a call module has been created.
 * @see interstellar/services/account_service/endpoints.py — `configure_freeswitch_account`
 */
export const configureFreeSwitchAccount = async (
  accountId: number,
  payload: ConfigureFreeSwitchPayload
): Promise<ConfigureFreeSwitchResponse> => {
  const response = await interstellarApi.post<ConfigureFreeSwitchResponse>(
    '/account/configure-freeswitch',
    payload,
    { params: { account_id: accountId } }
  );
  return response.data;
};

/* ───────────────────────── Shared FreeSWITCH ─────────────────── */

/** Time slot for schedule configuration */
export interface ScheduleTimeSlotInput {
  day_of_week: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
  is_open: boolean;
  open_time: string; // HH:MM format
  close_time: string; // HH:MM format
}

/** Payload for POST /account/shared — create account in shared FreeSWITCH */
export interface CreateSharedAccountPayload {
  name: string;
  phone_number: number;
  call_rescue_number: number;
  cosmos_account_id: string;
  freeswitch_group_id: number;
  admin_username: string;
  admin_password: string;
  search_index_name?: string;
  /** IANA timezone (e.g. 'America/Puerto_Rico') for schedule time conversion */
  timezone?: string;
  /** Custom schedule time slots. If not provided, defaults to Mon-Sun 8am-6pm */
  schedule_time_slots?: ScheduleTimeSlotInput[];
  /** Defaults to 'twilio'; the FreeSWITCH must have the carrier's gateway or creation fails with 409. */
  outbound_carrier?: OutboundCarrier;
}

/** Response from POST /account/shared */
export interface CreateSharedAccountResponse {
  id: number;
  message: string;
  freeswitch_group_id: number;
  freeswitch_group_name: string;
  /** Auto-created default schedule ID (Mon-Sun 8am-6pm) */
  default_schedule_id: number;
}

/**
 * Creates an account that uses a shared FreeSWITCH instance.
 * @see interstellar/services/account_service/endpoints.py — `create_shared_account`
 */
export const createSharedAccount = async (
  payload: CreateSharedAccountPayload
): Promise<CreateSharedAccountResponse> => {
  const response = await interstellarApi.post<CreateSharedAccountResponse>(
    '/account/shared',
    payload
  );
  return response.data;
};

/** User to create in a shared account */
export interface SharedAccountUser {
  cosmos_user_id: number;
  phone: number;
  is_admin: boolean;
  /** If true (default), user can receive inbound calls via call center queues */
  in_call_center?: boolean;
}

/** Payload for POST /account/shared/users */
export interface CreateSharedAccountUsersPayload {
  users: SharedAccountUser[];
}

/** Response from POST /account/shared/users */
export interface CreateSharedAccountUsersResponse {
  created: number;
  users: Array<{ id: number; extension: number; in_call_center: boolean }>;
}

/**
 * Creates users in an account that uses shared FreeSWITCH.
 * @see interstellar/services/account_service/endpoints.py — `create_shared_account_users`
 */
export const createSharedAccountUsers = async (
  accountId: number,
  payload: CreateSharedAccountUsersPayload
): Promise<CreateSharedAccountUsersResponse> => {
  const response = await interstellarApi.post<CreateSharedAccountUsersResponse>(
    '/account/shared/users',
    payload,
    { params: { account_id: accountId } }
  );
  return response.data;
};

/* ───────────────────────── Shared Account Modules ─────────────────── */

/** In-office configuration for a shared account module */
export interface SharedModuleInOfficeConfig {
  ring_strategy:
    | 'round_robin'
    | 'fixed_order'
    | 'longest_idle_agent'
    | 'ring_all'
    | 'random'
    | 'agent_with_least_talk_time'
    | 'agent_with_fewest_calls';
  greeting_playback_id: number;
  hold_music_playback_id: number;
  announce_playback_id: number;
  announce_frequency?: number;
  max_wait_time?: number;
  max_calls_on_wait?: number;
}

/** Out-of-office configuration for a shared account module */
export interface SharedModuleOutOfOfficeConfig {
  message_playback_id: number;
}

/** Call group to create for the module's agents */
export interface SharedModuleCallGroup {
  name: string;
  user_ids: number[];
}

/** Payload for POST /account/shared/modules */
export interface CreateSharedModulePayload {
  name: string;
  phone_number: string;
  schedule_id: number;
  call_group: SharedModuleCallGroup;
  in_office: SharedModuleInOfficeConfig;
  out_of_office: SharedModuleOutOfOfficeConfig;
}

/** Response from POST /account/shared/modules */
export interface CreateSharedModuleResponse {
  message: string;
  id: number;
  call_group_id: number;
  dialplan_file: string;
  context: string;
}

/**
 * Creates a call module for an account on a shared FreeSWITCH instance.
 *
 * This creates the full module stack: CallGroup, CallModule, configs,
 * deploys the dialplan, and reloads FreeSWITCH.
 *
 * @see interstellar/services/account_service/endpoints.py — `create_shared_account_module`
 */
export const createSharedModule = async (
  accountId: number,
  payload: CreateSharedModulePayload
): Promise<CreateSharedModuleResponse> => {
  const response = await interstellarApi.post<CreateSharedModuleResponse>(
    '/account/shared/modules',
    payload,
    { params: { account_id: accountId } }
  );
  return response.data;
};

/* ───────────────────────── Shared Account Cleanup ─────────────────── */

/** Response from DELETE /account/shared/cleanup-freeswitch */
export interface CleanupSharedAccountResponse {
  message: string;
  id: number;
  removed: {
    users: number;
    dialplan_files: string[];
    queues_domain: string;
  };
}

/**
 * Cleans up FreeSWITCH resources for a shared account before deletion.
 *
 * This removes users, dialplan files, and call center queues from the shared
 * FreeSWITCH instance. Must be called before `deleteAccount` for accounts
 * that have `freeswitch_group_id` set.
 *
 * @see interstellar/services/account_service/endpoints.py — `cleanup_shared_account_freeswitch`
 */
export const cleanupSharedAccountFreeSwitch = async (
  accountId: number
): Promise<CleanupSharedAccountResponse> => {
  const response = await interstellarApi.delete<CleanupSharedAccountResponse>(
    '/account/shared/cleanup-freeswitch',
    { params: { account_id: accountId } }
  );
  return response.data;
};

/* ───────────────────────── Regenerate FreeSWITCH Config ─────────────────── */

/** Response from POST /account/shared/regenerate-freeswitch */
export interface RegenerateFreeSwitchResponse {
  message: string;
  id: number;
  freeswitch_group_id: number;
  sip_domain: string;
  context: string;
  regenerated: {
    domain_xml: boolean;
    shared_outbound_routing: boolean;
    tenant_router: boolean;
    outgoing_dialplan: boolean;
    user_xmls: number;
    callcenter_base: boolean;
  };
  tenant_routes_count: number;
}

/**
 * Regenerates all FreeSWITCH configuration for a shared account.
 *
 * This regenerates: domain XML, shared outbound routing, tenant router,
 * outgoing dialplan, user XMLs, and call center config. Useful when
 * FreeSWITCH XMLs are out of sync with the database.
 *
 * @see interstellar/services/account_service/endpoints.py — `regenerate_shared_account_freeswitch`
 */
export const regenerateSharedAccountFreeSwitch = async (
  accountId: number
): Promise<RegenerateFreeSwitchResponse> => {
  const response = await interstellarApi.post<RegenerateFreeSwitchResponse>(
    '/account/shared/regenerate-freeswitch',
    null,
    { params: { account_id: accountId } }
  );
  return response.data;
};

/* ───────────────────────── Shared Account IVR ─────────────────── */

/** Audio configuration for an IVR option */
export interface IvrOptionAudio {
  language: 'es' | 'en';
  selection_audio_url?: string;
  response_audio_url?: string;
}

/** Single IVR menu option */
export interface IvrOption {
  digit: string;
  label: string;
  target_module_id?: number;
  /**
   * 'module' routes to a call module; 'replay' replays the current menu;
   * 'submenu' opens `options`; 'back' returns to the parent menu
   */
  action_type?: 'module' | 'replay' | 'submenu' | 'back';
  audios?: IvrOptionAudio[];
  /** Options of the submenu (action_type 'submenu') */
  options?: IvrOption[];
  /** Intro audio of the submenu */
  menu_audio_url?: string;
}

/** DID to update for IVR routing */
export interface IvrDid {
  phone_number: string;
  call_module_id?: number;
}

/** Payload for POST /account/shared/configure-ivr */
export interface ConfigureIvrPayload {
  enabled: boolean;
  language?: 'es' | 'en';
  welcome_audio_url?: string;
  invalid_audio_url?: string;
  timeout_ms?: number;
  max_failures?: number;
  options?: IvrOption[];
  /** DIDs to update for IVR routing */
  dids?: IvrDid[];
}

/** Response from POST /account/shared/configure-ivr */
export interface ConfigureIvrResponse {
  message: string;
  enabled: boolean;
  options_count: number;
  dids_updated: number;
}

/**
 * Configures IVR menu for a shared FreeSWITCH account.
 *
 * The IVR plays a welcome message, collects a digit, and routes
 * to the appropriate call module based on the selection.
 *
 * @see interstellar/services/account_service/endpoints.py — `configure_shared_ivr`
 */
export const configureSharedIvr = async (
  accountId: number,
  payload: ConfigureIvrPayload
): Promise<ConfigureIvrResponse> => {
  const response = await interstellarApi.post<ConfigureIvrResponse>(
    '/account/shared/configure-ivr',
    payload,
    { params: { account_id: accountId } }
  );
  return response.data;
};
